import { useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Flex, Text } from 'vvs-uikit'
import { QuestCategory, QuestSymbol } from 'state/vvsgotchi/models'
import { useTranslation } from 'contexts/Localization'
import { CloseButton } from 'components/CloseButton'
import useEndOfUTCDayCountdown from 'hooks/useEndOfUTCDayCountdown'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { QuestFrequency } from 'state/vvsgotchi/models/quest'
import { isGoldDiggerDisabled } from 'state/vvsgotchi/quests/goldDigger'
import { questSymbolToImageUrl, questSymbolToImagePadding, questCategoryDetails } from 'views/VvsGotchi/config'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { QuestDetailNotice } from 'views/VvsGotchi/components/QuestDetailNotice'
import QuestDetailTabs from 'views/VvsGotchi/components/QuestDetailTabs'
import ScreenFooter from 'views/VvsGotchi/components/ScreenFooter'
import { QuestCallToActionButton } from 'views/VvsGotchi/components/QuestCallToAction'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { Path } from 'config/constants/paths'

import GoldDiggerQuestDetailNotice from 'views/VvsGotchi/components/QuestDetailNotice/GoldDigger'

import { QuestDetailPageProps } from './types'

import { QuestDetailBanner } from './styles'
import { LightPageContainer } from '../styles'

const DefaultQuestDetailPage: React.FC<QuestDetailPageProps> = ({ quest }) => {
  const { t } = useTranslation()
  const { isOnboarded, owner, refresh } = useGotchiOwner()
  const history = useHistory()

  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(
    () => () => {
      // onUnmount
      refreshRef.current()
    },
    [],
  )
  const onboardToUnlock = !isOnboarded && quest?.category !== QuestCategory.ONBOARDING

  const isGoldDigger = quest?.symbol === QuestSymbol.GOLD_DIGGER
  const isGoldDiggerAndDisabled = isGoldDigger && isGoldDiggerDisabled()

  const disabled = !quest || onboardToUnlock || isGoldDiggerAndDisabled

  return (
    <LightPageContainer>
      <QuestDetailBanner bg={questCategoryDetails[quest?.category]?.bgImageUrl} disabled={disabled}>
        <Flex justifyContent="end" mb="8px">
          <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_QUEST)} />
        </Flex>
        <Flex>
          <Flex flex="1" flexDirection="column">
            {quest?.name && (
              <Text color="black" fontSize="28px" fontWeight="600">
                {quest.name}
              </Text>
            )}
            {quest?.description && <Text color="black">{quest.description}</Text>}
          </Flex>
          <PixelatedImage
            src={questSymbolToImageUrl[quest?.symbol] ?? '/images/vvsGotchi/mole_controller.png'}
            height="72px"
            padding={questSymbolToImagePadding[quest?.symbol]}
            alt={quest?.name}
            grayout={disabled}
          />
        </Flex>
      </QuestDetailBanner>

      {onboardToUnlock ? (
        <QuestDetailNotice type="locked">{t('Unlocks after completion of on-boarding')}</QuestDetailNotice>
      ) : isGoldDigger ? (
        <GoldDiggerQuestDetailNotice quest={quest} />
      ) : (
        quest?.isCompleted &&
        (quest?.frequency === QuestFrequency.ONCE_A_DAY ? (
          <QuestResetNotice />
        ) : (
          <QuestDetailNotice type="success">
            {quest.category === QuestCategory.ONBOARDING
              ? t("Congrats! You have completed this task. Let's go on more quests and start earning EXP!")
              : t("Congrats! You have completed this task. Let's go on more quests and keep earning EXP!")}
          </QuestDetailNotice>
        ))
      )}

      <QuestDetailTabs owner={owner} quest={quest} />

      <ScreenFooter background="black">
        <QuestCallToActionButton quest={quest} disabled={disabled} />
      </ScreenFooter>
    </LightPageContainer>
  )
}

export default DefaultQuestDetailPage

const QuestResetNotice = () => {
  const { t } = useTranslation()
  const { refresh } = useGotchiOwner()
  const timeExcerptMap = useEndOfUTCDayCountdown(refresh)

  return (
    <QuestDetailNotice type="coming-soon">
      {t('The quest will be refreshed in %untilResetTime%', {
        untilResetTime: timeExcerptMap.h > 0 ? t('%h%hr %m%m', timeExcerptMap) : t('%m%m', timeExcerptMap),
      })}
    </QuestDetailNotice>
  )
}
